import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormGroup,
    FormLabel,
    Grid,
    MenuItem,
    Radio,
    RadioGroup,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
    useMediaQuery,
    useTheme,
    Chip,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import {
    maxPageWidth,
    valueDiabetesForm,
    valueHipertensionForm,
    valueHipotoroidismoForm,
    valueObesidadForm,
} from "../theme/configurationVariables";
import { DatePicker } from "@mui/x-date-pickers";
import { Cancel, ExpandMore } from "@mui/icons-material";
import { useCatalog } from "../context/CatalogContext";
import {
    acceptStyle,
    baseStyle,
    focusedStyle,
    rejectStyle,
    useQuestion,
} from "../context/QuestionContext";
import IconDiabetesPng from "../img/icon-diabetes.png";
import IconHipertension from "../img/icon-hipertension.png";
import IconObesidad from "../img/icon-obesidad.png";
import IconHipotiroidismo from "../img/icon-hipotoroidismo.png";
import IconHeyDoctor from "../img/icon-heydoctor.png";
import IconHeyDoctorShort from "../img/icon-heydoctor-short.png";
import IconWhatsapp from "../img/icon-whatsapp.png";
import { Link as LinkRouter } from "react-router";

/* ─── Design tokens ─────────────────────────────────────────────────── */
const TEAL = "#4abcc9";
const TEAL2 = "#257590";
const NAVY = "#0d1f2d";
const NAVY2 = "#1a3a4a";
const BG = "#f0f7f9";
const BORDER = "#d6edf2";
const LIGHT = "#eef6f8";

const cardSx = {
    background: "#fff",
    borderRadius: "18px",
    border: `1.5px solid ${BORDER}`,
    overflow: "hidden",
};

const cardHeaderSx = {
    background: `linear-gradient(90deg, rgba(74,188,201,.06), transparent)`,
    borderBottom: `1.5px solid ${BORDER}`,
    px: 3,
    py: 2,
    display: "flex",
    alignItems: "center",
    gap: 1.5,
};

const cardBodySx = { px: 3, py: 3 };

const iconCircleSx = {
    width: 38,
    height: 38,
    minWidth: 38,
    borderRadius: "10px",
    background: `rgba(74,188,201,.12)`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    overflow: "hidden",
};

const optionalBadgeSx = {
    display: "inline-block",
    background: "rgba(74,188,201,.1)",
    color: TEAL2,
    fontSize: ".68rem",
    fontWeight: 700,
    letterSpacing: "1px",
    textTransform: "uppercase" as const,
    borderRadius: "99px",
    px: 1,
    py: 0.25,
    ml: 1,
};

/** Chip toggle reutilizable para catálogos */
function AllergyChip({
    label,
    selected,
    onClick,
}: {
    label: string;
    selected: boolean;
    onClick: () => void;
}) {
    return (
        <Box
            component="span"
            onClick={onClick}
            sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: "5px",
                px: 1.75,
                py: 0.875,
                border: `1.5px solid ${selected ? TEAL : BORDER}`,
                borderRadius: "99px",
                background: selected ? "rgba(74,188,201,.12)" : "#fff",
                color: selected ? TEAL2 : "#6b7280",
                fontFamily: "'Manrope', sans-serif",
                fontSize: ".8rem",
                fontWeight: 600,
                cursor: "pointer",
                userSelect: "none",
                transition: "all .15s",
                "&:hover": {
                    borderColor: TEAL,
                    color: TEAL2,
                    background: "rgba(74,188,201,.06)",
                },
                "&::before": selected ? { content: '"✓"', fontSize: ".7rem", fontWeight: 900 } : {},
            }}
        >
            {selected && (
                <Box component="span" sx={{ fontSize: ".7rem", fontWeight: 900, mr: 0.25 }}>
                    ✓
                </Box>
            )}
            {label}
        </Box>
    );
}

export default function PerfilamientoFormPage() {
    const theme = useTheme();
    const matches = useMediaQuery(theme.breakpoints.up("sm"));

    const { antibioticCatalog, foodCatalog, medicineCatalog, stateCatalog } = useCatalog();

    const {
        questionForm,
        dropzoneInput,
        fileUploadList,
        questionErrorForm,
        handleChangeQuestionForm,
        handleChangeQuestionFormDate,
        handleClickDeleteFile,
        handleChangeQuestionFormAnyField,
        handleChangeQuestionFormCheck,
        handleClickSubmitQuestion,
    } = useQuestion();

    const {
        pregunta,
        alergiaLatex,
        alergiasAlimentos,
        alergiasAntibioticos,
        alergiasMedicamentos,
        correo,
        enfermedades,
        fechaNacimiento,
        idEstado,
        idMedioContacto,
        idSexo,
        medicamentoActual,
        otraAlergiaAlimento,
        otraAlergiaAntibiotico,
        otraAlergiaMedicamento,
        otraEnfermedad,
        aceptaTerminosCondiciones,
    } = questionForm;

    const { isDragActive, isDragAccept, isDragReject, getRootProps, getInputProps, open } =
        dropzoneInput;

    const styleDropzone = useMemo(
        () => ({
            ...baseStyle,
            ...(isDragActive ? focusedStyle : {}),
            ...(isDragAccept ? acceptStyle : {}),
            ...(isDragReject ? rejectStyle : {}),
        }),
        [isDragActive, isDragAccept, isDragReject]
    );

    useEffect(() => {
        window.scrollTo({ left: 0, top: 0, behavior: "smooth" });
    }, []);

    /* ─── Active stepper step via IntersectionObserver ───────────────── */
    const [activeStep, setActiveStep] = useState(1);

    useEffect(() => {
        const SECTION_IDS = ["sec-1", "sec-2", "sec-3", "sec-4", "sec-5", "sec-6"];
        const observer = new IntersectionObserver(
            (entries) => {
                // Toma el que más visible esté
                const visible = entries
                    .filter((e) => e.isIntersecting)
                    .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
                if (visible.length > 0) {
                    const idx = SECTION_IDS.indexOf(visible[0].target.id);
                    if (idx !== -1) setActiveStep(idx + 1);
                }
            },
            { threshold: 0.25, rootMargin: "-60px 0px -40% 0px" }
        );
        SECTION_IDS.forEach((id) => {
            const el = document.getElementById(id);
            if (el) observer.observe(el);
        });
        return () => observer.disconnect();
    }, []);

    /* Helpers para chips de alergias — los arrays del contexto son string[] */
    const toggleAliment = (id: string) =>
        handleChangeQuestionFormAnyField(
            "alergiasAlimentos",
            alergiasAlimentos.includes(id)
                ? alergiasAlimentos.filter((x) => x !== id)
                : [...alergiasAlimentos, id]
        );

    const toggleAntibiotic = (id: string) =>
        handleChangeQuestionFormAnyField(
            "alergiasAntibioticos",
            alergiasAntibioticos.includes(id)
                ? alergiasAntibioticos.filter((x) => x !== id)
                : [...alergiasAntibioticos, id]
        );

    const toggleMedicine = (id: string) =>
        handleChangeQuestionFormAnyField(
            "alergiasMedicamentos",
            alergiasMedicamentos.includes(id)
                ? alergiasMedicamentos.filter((x) => x !== id)
                : [...alergiasMedicamentos, id]
        );

    /* ─── Render ─────────────────────────────────────────────────────── */
    return (
        <Box sx={{ background: BG, minHeight: "100vh", fontFamily: "'Manrope', sans-serif" }}>

            {/* ── HERO ── */}
            <Box
                sx={{
                    background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY2} 60%, #0e4a5a 100%)`,
                    position: "relative",
                    overflow: "hidden",
                    px: 3,
                    pt: "52px",
                    pb: "40px",
                }}
            >
                {/* dot pattern */}
                <Box
                    sx={{
                        position: "absolute",
                        inset: 0,
                        opacity: 0.07,
                        backgroundImage: "radial-gradient(circle, #fff 1px, transparent 1px)",
                        backgroundSize: "32px 32px",
                    }}
                />
                {/* glow */}
                <Box
                    sx={{
                        position: "absolute",
                        right: -60,
                        top: -60,
                        width: 380,
                        height: 380,
                        background: "radial-gradient(circle, rgba(74,188,201,.2) 0%, transparent 65%)",
                        pointerEvents: "none",
                    }}
                />
                <Box
                    sx={{
                        maxWidth: maxPageWidth,
                        mx: "auto",
                        position: "relative",
                        zIndex: 2,
                        display: "flex",
                        alignItems: "center",
                        gap: 3.5,
                    }}
                >
                    <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 2 }}>
                            <Box sx={{ width: 32, height: 3, background: TEAL, borderRadius: 2 }} />
                            <Typography
                                sx={{
                                    color: TEAL,
                                    fontFamily: "'Montserrat', sans-serif",
                                    fontSize: ".72rem",
                                    fontWeight: 700,
                                    letterSpacing: "2px",
                                    textTransform: "uppercase",
                                }}
                            >
                                Consulta Médica
                            </Typography>
                        </Box>
                        <Typography
                            sx={{
                                fontFamily: "'Montserrat', sans-serif",
                                fontSize: "clamp(2.4rem, 5vw, 4rem)",
                                fontWeight: 900,
                                color: "#fff",
                                lineHeight: 1.1,
                                mb: 1.25,
                                letterSpacing: "-1px",
                            }}
                        >
                            Tu médico necesita<br />conocerte primero
                        </Typography>
                        <Typography sx={{ color: "rgba(255,255,255,.62)", fontSize: ".92rem", lineHeight: 1.6, maxWidth: 480 }}>
                            Cuanto más sepamos sobre tu salud, mejor preparado llegará el médico a tu consulta. Solo toma unos minutos.
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            flexShrink: 0,
                            width: 88,
                            height: 88,
                            borderRadius: "22px",
                            background: "rgba(74,188,201,.12)",
                            border: "1px solid rgba(74,188,201,.28)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <span className="material-symbols-outlined" style={{ fontSize: 44, color: "#4abcc9", lineHeight: 1, display: "block", fontVariationSettings: "'FILL' 0, 'wght' 300, 'GRAD' 0, 'opsz' 48" }}>stethoscope</span>
                    </Box>
                </Box>
            </Box>



            {/* ── BODY ── */}
            <Box sx={{ maxWidth: maxPageWidth, mx: "auto", px: { xs: 2, md: 3 }, py: 5, pb: 12, display: "flex", gap: 4, alignItems: "flex-start" }}>

                {/* ── SIDEBAR STEPPER ── */}
                <Box
                    sx={{
                        width: 240,
                        flexShrink: 0,
                        display: { xs: "none", lg: "block" },
                        position: "sticky",
                        top: 24,
                    }}
                >
                    <Box sx={{ background: "#fff", borderRadius: "16px", border: `1.5px solid ${BORDER}`, p: 2.5 }}>
                        <Box sx={{ fontSize: ".68rem", fontWeight: 700, letterSpacing: "1.5px", color: "#9ca3af", textTransform: "uppercase", fontFamily: "'Montserrat',sans-serif", mb: 2 }}>
                            Secciones
                        </Box>
                        {[
                            { n: 1, label: "Tu pregunta" },
                            { n: 2, label: "Datos básicos" },
                            { n: 3, label: "Alergias" },
                            { n: 4, label: "Enfermedades" },
                            { n: 5, label: "Canal de contacto" },
                            { n: 6, label: "Confirmar" },
                        ].map(({ n, label }) => {
                            const active = n === activeStep;
                            const done = n < activeStep;
                            return (
                                <Box
                                    key={n}
                                    onClick={() => {
                                        const el = document.getElementById(`sec-${n}`);
                                        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                                    }}
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1.5,
                                        py: 1,
                                        px: 1.25,
                                        borderRadius: "10px",
                                        mb: 0.25,
                                        background: active ? `rgba(74,188,201,.1)` : "transparent",
                                        borderLeft: active ? `3px solid ${TEAL}` : "3px solid transparent",
                                        cursor: "pointer",
                                        transition: "all .2s",
                                        "&:hover": { background: active ? `rgba(74,188,201,.12)` : `rgba(74,188,201,.04)` },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 28,
                                            height: 28,
                                            borderRadius: "50%",
                                            background: active ? TEAL : done ? "rgba(74,188,201,.15)" : "transparent",
                                            border: active ? "none" : done ? `2px solid ${TEAL}` : `2px solid #d1d5db`,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            fontSize: ".72rem",
                                            fontWeight: 800,
                                            color: active ? "#fff" : done ? TEAL2 : "#9ca3af",
                                            fontFamily: "'Montserrat',sans-serif",
                                            flexShrink: 0,
                                            transition: "all .25s",
                                        }}
                                    >
                                        {done ? "✓" : n}
                                    </Box>
                                    <Box
                                        sx={{
                                            fontSize: ".82rem",
                                            fontWeight: active ? 700 : 500,
                                            color: active ? NAVY : done ? TEAL2 : "#6b7280",
                                            fontFamily: "'Manrope',sans-serif",
                                            transition: "color .2s",
                                        }}
                                    >
                                        {label}
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                </Box>

                {/* ── CONTENT ── */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Grid container spacing={3}>

                        {/* ═══ S1: Tu pregunta ═══ */}
                        <Grid size={{ xs: 12 }}>
                            <Box id="sec-1" sx={cardSx}>
                                <Box sx={cardHeaderSx}>
                                    <Box sx={iconCircleSx}><span className="material-symbols-outlined" style={{ fontSize: 20, color: "#4abcc9", lineHeight: 1 }}>edit_note</span></Box>
                                    <Box>
                                        <Typography sx={{ fontFamily: "'Montserrat',sans-serif", fontSize: ".95rem", fontWeight: 700, color: NAVY }}>
                                            Tu motivo de consulta
                                        </Typography>
                                        <Typography sx={{ fontSize: ".78rem", color: "#6b7280", mt: 0.25 }}>
                                            Mientras más detalle des, mejor podrá ayudarte el médico
                                        </Typography>
                                    </Box>
                                </Box>
                                <Box sx={cardBodySx}>
                                    <Typography sx={{ fontSize: ".8rem", fontWeight: 700, color: NAVY, mb: 0.75 }}>
                                        ¿Qué te está pasando?{" "}
                                        <Box component="span" sx={{ color: TEAL }}>*</Box>
                                    </Typography>
                                    {/* ── Textarea wrap (borde sólido) ── */}
                                    <Box
                                        sx={{
                                            border: `1.5px solid ${questionErrorForm.pregunta ? "#ef4444" : BORDER}`,
                                            borderRadius: "12px",
                                            background: BG,
                                            transition: "border-color .2s",
                                            overflow: "hidden",
                                            "&:focus-within": { borderColor: TEAL, boxShadow: "0 0 0 3px rgba(74,188,201,.15)" },
                                        }}
                                    >
                                        <TextField
                                            name="pregunta"
                                            fullWidth
                                            multiline
                                            minRows={4}
                                            maxRows={10}
                                            autoFocus
                                            value={pregunta}
                                            error={questionErrorForm.pregunta}
                                            placeholder="Ej: Tengo 3 días con dolor de cabeza intenso en la frente, sensibilidad a la luz y fiebre leve. He tomado paracetamol sin mejoría…"
                                            onChange={handleChangeQuestionForm}
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    "& fieldset": { border: "none" },
                                                    background: "transparent",
                                                    fontFamily: "'Manrope', sans-serif",
                                                    fontSize: ".92rem",
                                                },
                                            }}
                                        />
                                        {fileUploadList.length > 0 && (
                                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, px: 2, pb: 1 }}>
                                                {fileUploadList.map((x, i) => (
                                                    <Chip
                                                        key={i}
                                                        label={x.name}
                                                        onDelete={() => handleClickDeleteFile(x)}
                                                        deleteIcon={<Cancel onMouseDown={(e) => e.stopPropagation()} />}
                                                        sx={{
                                                            background: "rgba(74,188,201,.12)",
                                                            color: TEAL2,
                                                            fontWeight: 600,
                                                            fontSize: ".78rem",
                                                            border: `1px solid ${TEAL}`,
                                                            "& .MuiChip-deleteIcon": { color: TEAL2 },
                                                        }}
                                                    />
                                                ))}
                                            </Box>
                                        )}
                                        {/* Footer: solo contador de chars */}
                                        <Box
                                            sx={{
                                                px: 2,
                                                py: 1.25,
                                                background: "#fff",
                                                borderTop: `1px solid ${BORDER}`,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "flex-end",
                                            }}
                                        >
                                            <Typography sx={{ fontSize: ".72rem", color: "#9ca3af" }}>
                                                {pregunta.length} / 1000
                                            </Typography>
                                        </Box>
                                    </Box>

                                    {questionErrorForm.pregunta && (
                                        <Typography sx={{ color: "#ef4444", fontSize: ".78rem", mt: 0.75 }}>
                                            La pregunta es requerida
                                        </Typography>
                                    )}

                                    {/* ── Dropzone separado (borde punteado) ── */}
                                    <div {...getRootProps()}>
                                        <input {...getInputProps()} />
                                        <Box
                                            sx={{
                                                mt: 1.5,
                                                border: `2px dashed ${BORDER}`,
                                                borderRadius: "10px",
                                                py: 2,
                                                textAlign: "center",
                                                cursor: "pointer",
                                                transition: "border-color .2s, background .2s",
                                                "&:hover": { borderColor: TEAL, background: "rgba(74,188,201,.04)" },
                                            }}
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="none" stroke={TEAL} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: "block", margin: "0 auto 4px" }}>
                                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                                <polyline points="14 2 14 8 20 8" />
                                                <line x1="12" y1="18" x2="12" y2="12" />
                                                <line x1="9" y1="15" x2="15" y2="15" />
                                            </svg>
                                            <Typography sx={{ fontSize: ".8rem", color: "#6b7280" }}>
                                                Arrastra o haz clic para adjuntar estudios, recetas o análisis
                                            </Typography>
                                            <Typography sx={{ fontSize: ".72rem", color: "#9ca3af", mt: 0.25 }}>
                                                PDF · JPG · PNG
                                            </Typography>
                                        </Box>
                                    </div>
                                </Box>
                            </Box>
                        </Grid>

                        {/* ═══ S2: Datos del paciente ═══ */}
                        <Grid size={{ xs: 12 }}>
                            <Box id="sec-2" sx={cardSx}>
                                <Box sx={cardHeaderSx}>
                                    <Box sx={iconCircleSx}><span className="material-symbols-outlined" style={{ fontSize: 20, color: "#4abcc9", lineHeight: 1 }}>person</span></Box>
                                    <Box>
                                        <Typography sx={{ fontFamily: "'Montserrat',sans-serif", fontSize: ".95rem", fontWeight: 700, color: NAVY }}>
                                            Datos del paciente
                                        </Typography>
                                        <Typography sx={{ fontSize: ".78rem", color: "#6b7280", mt: 0.25 }}>
                                            Necesitamos esto para que el médico valide tu caso
                                        </Typography>
                                    </Box>
                                </Box>
                                <Box sx={cardBodySx}>
                                    <Grid container spacing={2.5}>
                                        <Grid size={{ xs: 12, md: 6 }}>
                                            <TextField
                                                name="correo"
                                                label="Correo electrónico"
                                                required
                                                fullWidth
                                                value={correo}
                                                error={questionErrorForm.correo}
                                                helperText={questionErrorForm.correo && "El correo es requerido y debe ser válido"}
                                                onChange={handleChangeQuestionForm}
                                                sx={styledInputSx}
                                            />
                                        </Grid>
                                        <Grid size={{ xs: 12, md: 6 }}>
                                            <TextField
                                                name="idEstado"
                                                label="Estado de residencia"
                                                required
                                                select
                                                fullWidth
                                                value={idEstado}
                                                error={questionErrorForm.idEstado}
                                                helperText={questionErrorForm.idEstado && "El lugar de residencia es requerido"}
                                                onChange={handleChangeQuestionForm}
                                                sx={styledInputSx}
                                            >
                                                <MenuItem value="">Selecciona tu estado…</MenuItem>
                                                {stateCatalog
                                                    .filter((item) =>
                                                        item.nombreEstado &&
                                                        item.nombreEstado.toLowerCase() !== "string"
                                                    )
                                                    .map((item) => (
                                                        <MenuItem key={item.idEstado} value={item.idEstado}>
                                                            {item.nombreEstado}
                                                        </MenuItem>
                                                    ))}
                                            </TextField>
                                        </Grid>
                                        <Grid size={{ xs: 12, md: 6 }}>
                                            <DatePicker
                                                name="fechaNacimiento"
                                                label="Fecha de nacimiento"
                                                views={["year", "month", "day"]}
                                                yearsOrder="desc"
                                                openTo="year"
                                                format="dd-MMMM-yyyy"
                                                maxDate={new Date()}
                                                value={fechaNacimiento as Date | null}
                                                onChange={handleChangeQuestionFormDate("fechaNacimiento")}
                                                sx={{ width: "100%", ...styledInputSx }}
                                            />
                                        </Grid>
                                        <Grid size={{ xs: 12, md: 6 }}>
                                            <FormControl>
                                                <FormLabel
                                                    sx={{
                                                        fontSize: ".8rem",
                                                        fontWeight: 700,
                                                        color: NAVY,
                                                        mb: 1,
                                                        "&.Mui-focused": { color: TEAL },
                                                    }}
                                                >
                                                    Sexo
                                                </FormLabel>
                                                <RadioGroup name="idSexo" row value={idSexo} onChange={handleChangeQuestionForm}>
                                                    {[
                                                        { val: "1", label: "Masculino", disabled: false },
                                                        { val: "2", label: "Femenino", disabled: false },
                                                        { val: "0", label: "Prefiero no decir", disabled: true },
                                                    ].map((opt) => (
                                                        <FormControlLabel
                                                            key={opt.val}
                                                            value={opt.val}
                                                            disabled={opt.disabled}
                                                            control={
                                                                <Radio
                                                                    sx={{
                                                                        color: BORDER,
                                                                        "&.Mui-checked": { color: TEAL },
                                                                        "&.Mui-disabled": { color: BORDER },
                                                                    }}
                                                                />
                                                            }
                                                            label={<Typography sx={{ fontSize: ".84rem", fontWeight: 600 }}>{opt.label}</Typography>}
                                                        />
                                                    ))}
                                                </RadioGroup>
                                            </FormControl>
                                        </Grid>
                                    </Grid>
                                </Box>
                            </Box>
                        </Grid>

                        {/* ═══ S3: Alergias ═══ */}
                        <Grid size={{ xs: 12 }}>
                            <Box id="sec-3" sx={cardSx}>
                                <Box sx={cardHeaderSx}>
                                    <Box sx={iconCircleSx}><span className="material-symbols-outlined" style={{ fontSize: 20, color: "#4abcc9", lineHeight: 1 }}>psychiatry</span></Box>
                                    <Box>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                                            <Typography sx={{ fontFamily: "'Montserrat',sans-serif", fontSize: ".95rem", fontWeight: 700, color: NAVY }}>
                                                Alergias conocidas
                                            </Typography>
                                            <Box sx={optionalBadgeSx}>OPCIONAL</Box>
                                        </Box>
                                        <Typography sx={{ fontSize: ".78rem", color: "#6b7280", mt: 0.25 }}>
                                            El médico necesita saber esto para no recetar algo que te dañe
                                        </Typography>
                                    </Box>
                                </Box>
                                <Box sx={cardBodySx}>
                                    <Grid container spacing={1.5}>

                                        {/* Alimentos */}
                                        <Grid size={{ xs: 12 }}>
                                            <Accordion
                                                elevation={0}
                                                sx={{
                                                    border: `1.5px solid ${BORDER}`,
                                                    borderRadius: "12px !important",
                                                    "&:before": { display: "none" },
                                                    overflow: "hidden",
                                                }}
                                            >
                                                <AccordionSummary
                                                    expandIcon={<ExpandMore sx={{ color: TEAL }} />}
                                                    sx={{ background: "#fff", "&:hover": { background: LIGHT } }}
                                                >
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                                        <Box sx={{ ...iconCircleSx, background: LIGHT }}>🍎</Box>
                                                        <Typography sx={{ fontFamily: "'Montserrat',sans-serif", fontSize: ".88rem", fontWeight: 700, color: NAVY }}>
                                                            Alimentos
                                                        </Typography>
                                                        {alergiasAlimentos.length > 0 && (
                                                            <Box sx={{ ...optionalBadgeSx, background: "rgba(74,188,201,.18)" }}>
                                                                {alergiasAlimentos.length} seleccionado{alergiasAlimentos.length > 1 ? "s" : ""}
                                                            </Box>
                                                        )}
                                                    </Box>
                                                </AccordionSummary>
                                                <AccordionDetails sx={{ background: BG, borderTop: `1.5px solid ${BORDER}` }}>
                                                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1.5 }}>
                                                        {foodCatalog.map((item) => (
                                                            <AllergyChip
                                                                key={item.idAlimento}
                                                                label={item.nombreAlimento}
                                                                selected={alergiasAlimentos.includes(String(item.idAlimento))}
                                                                onClick={() => toggleAliment(String(item.idAlimento))}
                                                            />
                                                        ))}
                                                    </Box>
                                                    <TextField
                                                        name="otraAlergiaAlimento"
                                                        placeholder="Otro alimento que no está en la lista…"
                                                        value={otraAlergiaAlimento}
                                                        onChange={handleChangeQuestionForm}
                                                        fullWidth
                                                        size="small"
                                                        sx={styledInputSx}
                                                    />
                                                </AccordionDetails>
                                            </Accordion>
                                        </Grid>

                                        {/* Antibióticos */}
                                        <Grid size={{ xs: 12 }}>
                                            <Accordion
                                                elevation={0}
                                                sx={{
                                                    border: `1.5px solid ${BORDER}`,
                                                    borderRadius: "12px !important",
                                                    "&:before": { display: "none" },
                                                    overflow: "hidden",
                                                }}
                                            >
                                                <AccordionSummary
                                                    expandIcon={<ExpandMore sx={{ color: TEAL }} />}
                                                    sx={{ background: "#fff", "&:hover": { background: LIGHT } }}
                                                >
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                                        <Box sx={{ ...iconCircleSx, background: LIGHT }}>💊</Box>
                                                        <Typography sx={{ fontFamily: "'Montserrat',sans-serif", fontSize: ".88rem", fontWeight: 700, color: NAVY }}>
                                                            Antibióticos
                                                        </Typography>
                                                        {alergiasAntibioticos.length > 0 && (
                                                            <Box sx={{ ...optionalBadgeSx, background: "rgba(74,188,201,.18)" }}>
                                                                {alergiasAntibioticos.length} seleccionado{alergiasAntibioticos.length > 1 ? "s" : ""}
                                                            </Box>
                                                        )}
                                                    </Box>
                                                </AccordionSummary>
                                                <AccordionDetails sx={{ background: BG, borderTop: `1.5px solid ${BORDER}` }}>
                                                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1.5 }}>
                                                        {antibioticCatalog.map((item) => (
                                                            <AllergyChip
                                                                key={item.idAntibiotico}
                                                                label={item.nombreAntibiotico}
                                                                selected={alergiasAntibioticos.includes(String(item.idAntibiotico))}
                                                                onClick={() => toggleAntibiotic(String(item.idAntibiotico))}
                                                            />
                                                        ))}
                                                    </Box>
                                                    <TextField
                                                        name="otraAlergiaAntibiotico"
                                                        placeholder="Otro antibiótico que no está en la lista…"
                                                        value={otraAlergiaAntibiotico}
                                                        onChange={handleChangeQuestionForm}
                                                        fullWidth
                                                        size="small"
                                                        sx={styledInputSx}
                                                    />
                                                </AccordionDetails>
                                            </Accordion>
                                        </Grid>

                                        {/* Medicamentos */}
                                        <Grid size={{ xs: 12 }}>
                                            <Accordion
                                                elevation={0}
                                                sx={{
                                                    border: `1.5px solid ${BORDER}`,
                                                    borderRadius: "12px !important",
                                                    "&:before": { display: "none" },
                                                    overflow: "hidden",
                                                }}
                                            >
                                                <AccordionSummary
                                                    expandIcon={<ExpandMore sx={{ color: TEAL }} />}
                                                    sx={{ background: "#fff", "&:hover": { background: LIGHT } }}
                                                >
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                                        <Box sx={{ ...iconCircleSx, background: LIGHT }}>🩺</Box>
                                                        <Typography sx={{ fontFamily: "'Montserrat',sans-serif", fontSize: ".88rem", fontWeight: 700, color: NAVY }}>
                                                            Medicamentos
                                                        </Typography>
                                                        {alergiasMedicamentos.length > 0 && (
                                                            <Box sx={{ ...optionalBadgeSx, background: "rgba(74,188,201,.18)" }}>
                                                                {alergiasMedicamentos.length} seleccionado{alergiasMedicamentos.length > 1 ? "s" : ""}
                                                            </Box>
                                                        )}
                                                    </Box>
                                                </AccordionSummary>
                                                <AccordionDetails sx={{ background: BG, borderTop: `1.5px solid ${BORDER}` }}>
                                                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1.5 }}>
                                                        {medicineCatalog.map((item) => (
                                                            <AllergyChip
                                                                key={item.idMedicamento}
                                                                label={item.nombreMedicamento}
                                                                selected={alergiasMedicamentos.includes(String(item.idMedicamento))}
                                                                onClick={() => toggleMedicine(String(item.idMedicamento))}
                                                            />
                                                        ))}
                                                    </Box>
                                                    <TextField
                                                        name="otraAlergiaMedicamento"
                                                        placeholder="Otro medicamento que no está en la lista…"
                                                        value={otraAlergiaMedicamento}
                                                        onChange={handleChangeQuestionForm}
                                                        fullWidth
                                                        size="small"
                                                        sx={styledInputSx}
                                                    />
                                                </AccordionDetails>
                                            </Accordion>
                                        </Grid>

                                        {/* Látex */}
                                        <Grid size={{ xs: 12 }}>
                                            <Accordion
                                                elevation={0}
                                                sx={{
                                                    border: `1.5px solid ${BORDER}`,
                                                    borderRadius: "12px !important",
                                                    "&:before": { display: "none" },
                                                    overflow: "hidden",
                                                }}
                                            >
                                                <AccordionSummary
                                                    expandIcon={<ExpandMore sx={{ color: TEAL }} />}
                                                    sx={{ background: "#fff", "&:hover": { background: LIGHT } }}
                                                >
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                                        <Box sx={{ ...iconCircleSx, background: LIGHT }}>🧤</Box>
                                                        <Typography sx={{ fontFamily: "'Montserrat',sans-serif", fontSize: ".88rem", fontWeight: 700, color: NAVY }}>
                                                            Látex
                                                        </Typography>
                                                        {alergiaLatex && (
                                                            <Box sx={{ ...optionalBadgeSx, background: "rgba(74,188,201,.18)" }}>
                                                                Marcado
                                                            </Box>
                                                        )}
                                                    </Box>
                                                </AccordionSummary>
                                                <AccordionDetails sx={{ background: BG, borderTop: `1.5px solid ${BORDER}` }}>
                                                    <FormGroup>
                                                        <FormControlLabel
                                                            control={
                                                                <Checkbox
                                                                    name="alergiaLatex"
                                                                    checked={alergiaLatex}
                                                                    onChange={handleChangeQuestionFormCheck}
                                                                    sx={{ color: BORDER, "&.Mui-checked": { color: TEAL } }}
                                                                />
                                                            }
                                                            label={
                                                                <Typography sx={{ fontSize: ".84rem", color: "#6b7280", fontFamily: "'Manrope',sans-serif" }}>
                                                                    Soy alérgico/a al látex (guantes, condones, globos, etc.)
                                                                </Typography>
                                                            }
                                                        />
                                                    </FormGroup>
                                                </AccordionDetails>
                                            </Accordion>
                                        </Grid>
                                    </Grid>

                                    {/* Medicamento actual */}
                                    <Box sx={{ mt: 2 }}>
                                        <Typography sx={{ fontSize: ".8rem", fontWeight: 700, color: NAVY, mb: 0.75 }}>
                                            ¿Tomas actualmente algún medicamento?
                                        </Typography>
                                        <TextField
                                            name="medicamentoActual"
                                            placeholder="Ej: Metformina 500 mg diarios, Omeprazol 20 mg…"
                                            value={medicamentoActual}
                                            onChange={handleChangeQuestionForm}
                                            fullWidth
                                            sx={styledInputSx}
                                        />
                                    </Box>
                                </Box>
                            </Box>
                        </Grid>

                        {/* ═══ S4: Antecedentes de salud ═══ */}
                        <Grid size={{ xs: 12 }}>
                            <Box id="sec-4" sx={cardSx}>
                                <Box sx={cardHeaderSx}>
                                    <Box sx={iconCircleSx}><span className="material-symbols-outlined" style={{ fontSize: 20, color: "#4abcc9", lineHeight: 1 }}>favorite</span></Box>
                                    <Box>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                            <Typography sx={{ fontFamily: "'Montserrat',sans-serif", fontSize: ".95rem", fontWeight: 700, color: NAVY }}>
                                                Antecedentes de salud
                                            </Typography>
                                            <Box sx={optionalBadgeSx}>Opcional</Box>
                                        </Box>
                                        <Typography sx={{ fontSize: ".78rem", color: "#6b7280", mt: 0.25 }}>
                                            Condiciones que el médico debe conocer antes de tu consulta
                                        </Typography>
                                    </Box>
                                </Box>
                                <Box sx={cardBodySx}>
                                    <ToggleButtonGroup
                                        value={enfermedades}
                                        orientation={matches ? "horizontal" : "vertical"}
                                        color="primary"
                                        fullWidth
                                        onChange={(e, values) => handleChangeQuestionFormAnyField("enfermedades", values)}
                                        sx={{
                                            gap: 1.5,
                                            flexWrap: "wrap",
                                            "& .MuiToggleButton-root": {
                                                flex: 1,
                                                minWidth: 120,
                                                border: `2px solid ${BORDER} !important`,
                                                borderRadius: "14px !important",
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "center",
                                                gap: 1.25,
                                                py: 2.25,
                                                px: 1.5,
                                                color: NAVY,
                                                fontFamily: "'Montserrat',sans-serif",
                                                fontSize: ".72rem",
                                                fontWeight: 700,
                                                transition: "all .2s",
                                                "&.Mui-selected": {
                                                    background: "rgba(74,188,201,.08)",
                                                    borderColor: `${TEAL} !important`,
                                                    color: TEAL2,
                                                },
                                                "&:hover": {
                                                    background: "rgba(74,188,201,.04)",
                                                    borderColor: `rgba(74,188,201,.4) !important`,
                                                },
                                            },
                                        }}
                                    >
                                        <ToggleButton value={valueDiabetesForm}>
                                            <img src={IconDiabetesPng} alt="Diabetes" style={{ height: 40 }} />
                                            Diabetes Mellitus
                                        </ToggleButton>
                                        <ToggleButton value={valueHipertensionForm}>
                                            <img src={IconHipertension} alt="Hipertensión" style={{ height: 40 }} />
                                            Hipertensión Arterial
                                        </ToggleButton>
                                        <ToggleButton value={valueObesidadForm}>
                                            <img src={IconObesidad} alt="Obesidad" style={{ height: 40 }} />
                                            Obesidad
                                        </ToggleButton>
                                        <ToggleButton value={valueHipotoroidismoForm}>
                                            <img src={IconHipotiroidismo} alt="Hipotiroidismo" style={{ height: 40 }} />
                                            Hipotiroidismo
                                        </ToggleButton>
                                    </ToggleButtonGroup>

                                    <Box sx={{ mt: 2.5 }}>
                                        <Typography sx={{ fontSize: ".8rem", fontWeight: 700, color: NAVY, mb: 0.75 }}>
                                            Otra enfermedad
                                        </Typography>
                                        <TextField
                                            name="otraEnfermedad"
                                            placeholder="Escribe aquí si tienes otro padecimiento…"
                                            value={otraEnfermedad}
                                            onChange={handleChangeQuestionForm}
                                            fullWidth
                                            sx={styledInputSx}
                                        />
                                    </Box>
                                </Box>
                            </Box>
                        </Grid>

                        {/* ═══ S5: Canal de contacto ═══ */}
                        <Grid size={{ xs: 12 }}>
                            <Box id="sec-5" sx={cardSx}>
                                <Box sx={cardHeaderSx}>
                                    <Box sx={iconCircleSx}><span className="material-symbols-outlined" style={{ fontSize: 20, color: "#4abcc9", lineHeight: 1 }}>forum</span></Box>
                                    <Box>
                                        <Typography sx={{ fontFamily: "'Montserrat',sans-serif", fontSize: ".95rem", fontWeight: 700, color: NAVY }}>
                                            ¿Cómo quieres recibir la respuesta?{" "}
                                            <Box component="span" sx={{ color: TEAL }}>*</Box>
                                        </Typography>
                                        <Typography sx={{ fontSize: ".78rem", color: "#6b7280", mt: 0.25 }}>
                                            Elige el canal y te contactaremos cuando el médico esté listo
                                        </Typography>
                                    </Box>
                                </Box>
                                <Box sx={cardBodySx}>
                                    <ToggleButtonGroup
                                        value={idMedioContacto}
                                        orientation={matches ? "horizontal" : "vertical"}
                                        color="primary"
                                        fullWidth
                                        exclusive
                                        onChange={(e, value) =>
                                            handleChangeQuestionFormAnyField("idMedioContacto", !value ? idMedioContacto : value)
                                        }
                                        sx={{
                                            gap: 2,
                                            "& .MuiToggleButton-root": {
                                                flex: 1,
                                                border: `2px solid ${BORDER} !important`,
                                                borderRadius: "16px !important",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "flex-start",
                                                gap: 2,
                                                py: 2.25,
                                                px: 2.5,
                                                textTransform: "none",
                                                color: NAVY,
                                                transition: "all .2s",
                                                "&.Mui-selected": {
                                                    background: "rgba(74,188,201,.07)",
                                                    borderColor: `${TEAL} !important`,
                                                },
                                                "&.Mui-disabled": { opacity: 0.45 },
                                                "&:hover:not(.Mui-disabled)": {
                                                    background: "rgba(74,188,201,.03)",
                                                    borderColor: `rgba(74,188,201,.4) !important`,
                                                },
                                            },
                                        }}
                                    >
                                        <ToggleButton value="2">
                                            <Box
                                                sx={{
                                                    width: 50,
                                                    height: 50,
                                                    borderRadius: "14px",
                                                    background: "rgba(74,188,201,.12)",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    flexShrink: 0,
                                                }}
                                            >
                                                <img src={IconHeyDoctorShort} alt="HeyDoctor" style={{ height: 28, width: "auto", objectFit: "contain" }} />
                                            </Box>
                                            <Box sx={{ textAlign: "left" }}>
                                                <Typography sx={{ fontFamily: "'Montserrat',sans-serif", fontSize: ".88rem", fontWeight: 700, color: NAVY }}>
                                                    HeyDoctor
                                                </Typography>
                                                <Typography sx={{ fontFamily: "'Manrope',sans-serif", fontSize: ".78rem", fontWeight: 400, color: "#6b7280" }}>
                                                    Plataforma médica segura
                                                </Typography>
                                            </Box>
                                        </ToggleButton>
                                        <ToggleButton value="1" disabled>
                                            <Box
                                                sx={{
                                                    width: 50,
                                                    height: 50,
                                                    borderRadius: "14px",
                                                    background: "#d9fad8",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    flexShrink: 0,
                                                }}
                                            >
                                                <img src={IconWhatsapp} alt="WhatsApp" style={{ height: 30 }} />
                                            </Box>
                                            <Box sx={{ textAlign: "left" }}>
                                                <Typography sx={{ fontFamily: "'Montserrat',sans-serif", fontSize: ".88rem", fontWeight: 700 }}>
                                                    WhatsApp
                                                </Typography>
                                                <Typography sx={{ fontSize: ".76rem", color: "#9ca3af" }}>
                                                    Próximamente disponible
                                                </Typography>
                                            </Box>
                                        </ToggleButton>
                                    </ToggleButtonGroup>
                                </Box>
                            </Box>
                        </Grid>

                        {/* ═══ S6: Términos y envío ═══ */}
                        <Grid size={{ xs: 12 }}>
                            <Box id="sec-6" sx={cardSx}>
                                <Box sx={cardHeaderSx}>
                                    <Box sx={iconCircleSx}><span className="material-symbols-outlined" style={{ fontSize: 20, color: "#4abcc9", lineHeight: 1 }}>verified_user</span></Box>
                                    <Box>
                                        <Typography sx={{ fontFamily: "'Montserrat',sans-serif", fontSize: ".95rem", fontWeight: 700, color: NAVY }}>
                                            Último paso
                                        </Typography>
                                        <Typography sx={{ fontSize: ".78rem", color: "#6b7280", mt: 0.25 }}>
                                            Acepta y envía — el médico revisará tu caso pronto
                                        </Typography>
                                    </Box>
                                </Box>
                                <Box sx={cardBodySx}>
                                    <FormGroup>
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "flex-start",
                                                gap: 1.5,
                                                p: 2,
                                                background: BG,
                                                borderRadius: "12px",
                                                border: `1.5px solid ${questionErrorForm.aceptaTerminosCondiciones ? "#ef4444" : BORDER}`,
                                                cursor: "pointer",
                                                transition: "border-color .2s",
                                                "&:hover": { borderColor: TEAL },
                                            }}
                                        >
                                            <Checkbox
                                                name="aceptaTerminosCondiciones"
                                                checked={aceptaTerminosCondiciones}
                                                onChange={handleChangeQuestionFormCheck}
                                                sx={{
                                                    color: BORDER,
                                                    mt: "-2px",
                                                    "&.Mui-checked": { color: TEAL },
                                                }}
                                            />
                                            <Typography sx={{ fontSize: ".84rem", color: "#6b7280", lineHeight: 1.6, fontFamily: "'Manrope',sans-serif" }}>
                                                Acepto ser mayor de 18 años y estar de acuerdo con los{" "}
                                                <LinkRouter
                                                    to="/terminos-y-condiciones"
                                                    style={{ color: TEAL, fontWeight: 700, textDecoration: "none" }}
                                                >
                                                    términos y condiciones
                                                </LinkRouter>{" "}
                                                y el{" "}
                                                <LinkRouter
                                                    to="/aviso-de-privacidad"
                                                    style={{ color: TEAL, fontWeight: 700, textDecoration: "none" }}
                                                >
                                                    aviso de privacidad
                                                </LinkRouter>{" "}
                                                de HeyDoctor.{" "}
                                                <Box component="span" sx={{ color: TEAL }}>*</Box>
                                            </Typography>
                                        </Box>
                                        {questionErrorForm.aceptaTerminosCondiciones && (
                                            <Typography sx={{ color: "#ef4444", fontSize: ".78rem", mt: 0.75, ml: 0.5 }}>
                                                Es necesario aceptar los términos y condiciones para continuar
                                            </Typography>
                                        )}
                                    </FormGroup>

                                    <Typography sx={{ fontSize: ".76rem", color: "#9ca3af", mt: 1, ml: 0.5 }}>
                                        * Campos obligatorios
                                    </Typography>

                                    <Button
                                        fullWidth
                                        onClick={handleClickSubmitQuestion}
                                        sx={{
                                            mt: 3,
                                            py: 2,
                                            background: `linear-gradient(135deg, ${TEAL}, ${TEAL2})`,
                                            color: "#fff",
                                            borderRadius: "14px",
                                            fontFamily: "'Montserrat', sans-serif",
                                            fontSize: "1rem",
                                            fontWeight: 800,
                                            textTransform: "none",
                                            boxShadow: "0 6px 24px rgba(74,188,201,.38)",
                                            "&:hover": {
                                                transform: "translateY(-2px)",
                                                boxShadow: "0 10px 32px rgba(74,188,201,.45)",
                                                background: `linear-gradient(135deg, ${TEAL}, ${TEAL2})`,
                                            },
                                        }}
                                        startIcon={
                                            <Box component="span" sx={{ fontSize: "1.1rem" }}>
                                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M5 12h14M12 5l7 7-7 7" />
                                                </svg>
                                            </Box>
                                        }
                                    >
                                        Ir a mi asesoría
                                    </Button>

                                    <Typography sx={{ textAlign: "center", fontSize: ".76rem", color: "#9ca3af", mt: 1.5 }}>
                                        Un médico revisará tu caso y te contactará pronto ·{" "}
                                        <LinkRouter to="/aviso-de-privacidad" style={{ color: TEAL, fontWeight: 700, textDecoration: "none" }}>
                                            Aviso de privacidad
                                        </LinkRouter>
                                    </Typography>
                                </Box>
                            </Box>
                        </Grid>

                    </Grid>
                </Box>{/* end content */}
            </Box>{/* end body */}
        </Box>
    );
}

/* ─── Shared input style ─────────────────────────────────────────────── */
const styledInputSx = {
    "& .MuiOutlinedInput-root": {
        borderRadius: "10px",
        background: "#f0f7f9",
        fontFamily: "'Manrope', sans-serif",
        fontSize: ".88rem",
        "& fieldset": { borderColor: "#d6edf2", borderWidth: "1.5px" },
        "&:hover fieldset": { borderColor: "#4abcc9" },
        "&.Mui-focused fieldset": { borderColor: "#4abcc9", boxShadow: "0 0 0 3px rgba(74,188,201,.15)" },
    },
    "& .MuiInputLabel-root.Mui-focused": { color: "#4abcc9" },
};
